import React, {useState} from 'react';
import {Box, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button} from '@mui/material';
import {useHistory} from 'react-router-dom';
import SchoolDashboardShell from '../components/SchoolDashboard.comp';
import {NAVY, ORANGE, TEAL, PageHeader, BandChip, headCell, bodyCell} from '../components/widgets.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom} from '../schoolData';
import {RowCell} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Slides 42-43 — Learning Impact Tracker (By Program) and Learning Impact by
   COS Area. Both tables and every row are copied verbatim from the deck's
   own tables — no filter bar exists on either slide, so none is added here.
   Each "By Program" row's `code` links it to its Program Impact drill-down
   (slides 44-46) at ./ProgramImpact.page.jsx — no separate nav entry, same
   pattern as the Teacher/LC Insights page this was modeled on. */
/* Live data (SD-42-01, SD-43-01), in this page's own layout:
     By Program   SD.S_19.C_2   ·   By COS Area   SD.S_19.C_3
   A program's code for its drill-down is the Program cell's module_code — the key
   the datahouse's "Program Impact" dashboards use. A row without one has no link.
   Every column sorts from its header and filters once there are enough rows
   (QA 9 Oct 2026); the signal legend above each table is the column's rubric
   (QA LA-SD-9), drawn as the other tables draw their bands. */
const emptyRow = (cols, text) => (
    <TableRow>
        <TableCell colSpan={cols} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{text}</TableCell>
    </TableRow>
);
const signalChip = (signal) => (!signal || signal === '—' ? '—' : <BandChip label={signal} color={signalColor(signal)}/>);

const signalColor = (signal) => {
    if (signal.startsWith('Strong')) return TEAL;
    if (signal.startsWith('Moderate')) return '#EFA020';
    return '#6A7282';
};

const SIGNALS = ['Early', 'Moderate', 'Strong'];
const SignalLegend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2}}>
        {SIGNALS.map((s) => <BandChip key={s} label={s} color={signalColor(s)}/>)}
    </Box>
);

// The datahouse writes the signal with or without the word "Signal"; both forms keep the rubric's order.
const SIGNAL_ORDER = ['Strong', 'Strong Signal', 'Moderate', 'Moderate Signal', 'Early', 'Early Signal', 'Weak', 'Weak Signal'];
const PROGRAM_COLUMNS = [
    {key: 'name', label: 'Program', type: 'text', align: 'left'},
    {key: 'creator', label: 'Program Creator', type: 'text', align: 'left'},
    {key: 'targetArea', label: 'Target Area', type: 'text', align: 'left'},
    // "52 → 61" sorts by its text (the pre score first); a checklist of these pairs would not help.
    {key: 'prePost', label: 'Pre → Post', type: 'text', filterable: false, accessor: (r) => r.prePost},
    {key: 'passRate', label: 'Pass Rate', type: 'percent'},
    {key: 'impactRate', label: 'Learning Impact Rate', type: 'percent'},
    {key: 'signal', label: 'Learning Impact Signal', type: 'band', order: SIGNAL_ORDER},
];
const AREA_COLUMNS = [
    {key: 'name', label: 'Readiness Area', type: 'text', align: 'left'},
    {key: 'baseline', label: 'Baseline Score', type: 'number'},
    {key: 'classes', label: 'Classes Covered', type: 'number'},
    {key: 'students', label: 'Students Reached', type: 'number'},
    {key: 'pre', label: 'Avg. Pre-Assessment', type: 'number'},
    {key: 'post', label: 'Avg. Post-Assessment', type: 'number'},
    {key: 'impactRate', label: 'Avg. Impact Rate', type: 'percent'},
    {key: 'signal', label: 'Impact Signal', type: 'band', order: SIGNAL_ORDER},
];
const frame = {
    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};
const headerCells = (t, columns) => columns.map((c, i) => (
    <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: c.align || 'center', ...(i === 0 ? {pl: 2.5} : {})}}/>
));
const zebra = (i) => ({backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'});

const ByProgramTable = ({rows, onSelectProgram}) => {
    const t = useTableControls(rows, PROGRAM_COLUMNS);
    return (
        <>
            <SignalLegend/>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={frame}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headerCells(t, PROGRAM_COLUMNS)}
                            <TableCell sx={{...headCell, textAlign: 'center'}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(8, rows.length ? 'No row matches the filters.' : 'No deployed programs with impact data yet.')}
                        {t.rows.map((row, i) => (
                            <TableRow
                                key={`${row.code || row.name}-${rows.indexOf(row)}`}
                                onClick={() => row.code && onSelectProgram(row.code)}
                                sx={{
                                    cursor: row.code ? 'pointer' : 'default',
                                    ...zebra(i),
                                    '&:hover': {backgroundColor: 'rgba(255, 105, 0, 0.06)'},
                                }}
                            >
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.name}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.creator}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="prePost"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="passRate"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="impactRate"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>
                                    {row._suppressed?.signal ? <RowCell row={row} k="signal"/> : signalChip(row.signal)}
                                </TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>
                                    {row.code && <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => {e.stopPropagation(); onSelectProgram(row.code);}}
                                        sx={{textTransform: 'none', fontWeight: 700, borderColor: 'var(--brand-border, #E5E7EB)', color: NAVY, whiteSpace: 'nowrap'}}
                                    >
                                        View Impact
                                    </Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const ByCosAreaTable = ({rows}) => {
    const t = useTableControls(rows, AREA_COLUMNS);
    return (
        <>
            <SignalLegend/>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={frame}>
                <Table>
                    <TableHead>
                        <TableRow>{headerCells(t, AREA_COLUMNS)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && emptyRow(8, rows.length ? 'No row matches the filters.' : 'No learning impact recorded yet.')}
                        {t.rows.map((row, i) => (
                            <TableRow key={row.name} sx={zebra(i)}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.name}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="baseline"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.classes}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.students}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="pre"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}><RowCell row={row} k="post"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}><RowCell row={row} k="impactRate"/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>
                                    {row._suppressed?.signal ? <RowCell row={row} k="signal"/> : signalChip(row.signal)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const LEARNING_IMPACT_SECTION_CODE = 'S_19';
const CARD_SECTIONS = [LEARNING_IMPACT_SECTION_CODE];

const LearningImpactPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const history = useHistory();
    const selectProgram = (code) => history.push(`${history.location.pathname}/program-impact?program_code=${encodeURIComponent(code)}`);
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    const programRows = rowsFrom(findCard(dashboardData, LEARNING_IMPACT_SECTION_CODE, 'C_2'), {
        name: 'Program', creator: 'Program Creator', targetArea: 'Target Area', prePost: 'Pre → Post',
        passRate: 'Pass Rate %', impactRate: 'Learning Impact Rate %', signal: 'Learning Impact Signal',
    }).map((r) => ({...r, code: r._cell.name?.module_code || null}));
    const areaRows = rowsFrom(findCard(dashboardData, LEARNING_IMPACT_SECTION_CODE, 'C_3'), {
        name: 'COS Area', baseline: 'Baseline Score', classes: 'Classes Covered', students: 'Students Reached',
        pre: 'Avg. Pre', post: 'Avg. Post', impactRate: 'Avg. Impact Rate %', signal: 'Impact Signal',
    });

    return (
        <SchoolDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB',
                        mb: 2,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="By Program"/>
                    <Tab label="By COS Area"/>
                </Tabs>
            }
        >
            <PageHeader
                title={activeTab === 0 ? 'Learning Impact Tracker' : 'Learning Impact by COS Area'}
                subtitle={activeTab === 0
                    ? 'Program pre/post evidence mapped back to the original COS area as a Learning Impact Signal.'
                    : 'Class-level programs aggregate back to COS areas as a Learning Improvement Signal, not Confirmed COS Movement.'}
            />

            {activeTab === 0 ? <ByProgramTable rows={programRows} onSelectProgram={selectProgram}/> : <ByCosAreaTable rows={areaRows}/>}
        </SchoolDashboardShell>
    );
};

export default LearningImpactPage;
